import apiService from '../apiService';
import logger from '../utils/logger';

export interface AIEvaluationRequest {
  questionId: string;
  question: string;
  intent: string;
  rubric: {
    band5?: { definition: string; examples: string[] };
    band4: { definition: string; examples: string[] };
    band3: { definition: string; examples: string[] };
    band2: { definition: string; examples: string[] };
    band1: { definition: string; examples: string[] };
    band0: { definition: string; examples: string[] };
  };
  learnerAnswer: string;
  sourceContent?: string;
  standardId?: string;
  generatedByModel?: string;
  assessmentPromptId?: string;
  mode?: 'practice' | 'exam';
}

export interface AIEvaluationResponse {
  score: 0 | 1 | 2 | 3 | 4 | 5;
  confidence: number;  // 0.0 to 1.0 - AI's confidence in the score
  evidence: string;
  feedback: string;
  processingTime: number;
  modelUsed?: string;
}

export interface BatchEvaluationProgress {
  completed: number;
  total: number;
  percentage: number;
  currentQuestion?: string;
}

interface EvaluationCache {
  [key: string]: {
    result: AIEvaluationResponse;
    timestamp: number;
  };
}

class AIEvaluationService {
  private cache: EvaluationCache = {};
  private readonly CACHE_TTL = 3600000; // 1 hour in milliseconds
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second

  /**
   * Evaluate a single short answer with progress tracking
   */
  async evaluateSingleAnswer(
    request: AIEvaluationRequest,
    onProgress?: (percent: number) => void
  ): Promise<AIEvaluationResponse> {
    const cacheKey = this.getCacheKey(request.question, request.learnerAnswer);

    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      logger.info('Returning cached evaluation result');
      if (onProgress) onProgress(100);
      return cached;
    }

    // Start progress tracking
    if (onProgress) {
      this.simulateProgress(onProgress, 10000); // Simulate progress over ~10 seconds
    }

    try {
      const result = await this.evaluateWithRetry(request);

      // Cache the result
      this.addToCache(cacheKey, result);

      if (onProgress) onProgress(100);
      return result;
    } catch (error) {
      logger.error('Failed to evaluate answer after retries:', error);
      throw error;
    }
  }

  /**
   * Evaluate multiple answers sequentially (for exam mode)
   * Processes one at a time with delays to avoid rate limiting
   */
  async evaluateBatch(
    requests: AIEvaluationRequest[],
    onProgress?: (progress: BatchEvaluationProgress) => void
  ): Promise<AIEvaluationResponse[]> {
    if (requests.length === 0) {
      return [];
    }

    logger.info(`Starting sequential batch evaluation of ${requests.length} answers`);

    let completed = 0;
    const results: AIEvaluationResponse[] = new Array(requests.length);
    const DELAY_BETWEEN_REQUESTS = 300; // 300ms delay between requests

    // Process requests sequentially to avoid rate limiting
    for (let index = 0; index < requests.length; index++) {
      const request = requests[index];

      try {
        // Check cache first
        const cacheKey = this.getCacheKey(request.question, request.learnerAnswer);
        const cached = this.getFromCache(cacheKey);

        let result: AIEvaluationResponse;
        if (cached) {
          logger.info(`Using cached result for question ${index + 1}`);
          result = cached;
        } else {
          result = await this.evaluateWithRetry(request);
          this.addToCache(cacheKey, result);
        }

        results[index] = result;
        completed++;

        // Update progress
        if (onProgress) {
          onProgress({
            completed,
            total: requests.length,
            percentage: Math.round((completed / requests.length) * 100),
            currentQuestion: request.question.substring(0, 50) + '...'
          });
        }

        // Add delay between requests to avoid rate limiting (except after the last one)
        if (index < requests.length - 1) {
          await this.sleep(DELAY_BETWEEN_REQUESTS);
        }
      } catch (error) {
        logger.error(`Failed to evaluate question ${index + 1}:`, error);
        // Return a fallback result instead of failing the entire batch
        const fallbackResult: AIEvaluationResponse = {
          score: 0,
          confidence: 0, // Low confidence for error fallback
          evidence: 'Evaluation failed',
          feedback: 'We encountered an error evaluating this answer. Please review the rubric manually.',
          processingTime: 0
        };
        results[index] = fallbackResult;
        completed++;

        if (onProgress) {
          onProgress({
            completed,
            total: requests.length,
            percentage: Math.round((completed / requests.length) * 100)
          });
        }

        // Still add delay even after errors
        if (index < requests.length - 1) {
          await this.sleep(DELAY_BETWEEN_REQUESTS);
        }
      }
    }

    logger.info(`Batch evaluation complete: ${completed}/${requests.length} successful`);
    return results;
  }

  /**
   * Evaluate with exponential backoff retry logic
   */
  private async evaluateWithRetry(
    request: AIEvaluationRequest,
    attempt: number = 1
  ): Promise<AIEvaluationResponse> {
    const startTime = Date.now();

    try {
      // Make API call through apiService
      const response = await apiService.evaluateShortAnswer({
        model: request.generatedByModel,
        question: request.question,
        intent: request.intent,
        rubric: request.rubric,
        learnerAnswer: request.learnerAnswer,
        sourceContent: request.sourceContent,
        questionId: request.questionId,
        generatedByModel: request.generatedByModel,
        assessmentPromptId: request.assessmentPromptId,
        mode: request.mode
      });

      const processingTime = Date.now() - startTime;

      return {
        score: response.evaluation.score,
        confidence: response.evaluation.confidence ?? 0.8, // Fallback to 0.8 if not provided
        evidence: response.evaluation.evidence,
        feedback: response.evaluation.feedback,
        processingTime,
        modelUsed: response.modelUsed
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.warn(`Evaluation attempt ${attempt} failed: ${errorMessage}`);

      // If we haven't exceeded max retries, try again with exponential backoff
      if (attempt < this.MAX_RETRIES) {
        const delay = this.RETRY_DELAY * Math.pow(2, attempt - 1);
        logger.info(`Retrying in ${delay}ms...`);

        await this.sleep(delay);
        return this.evaluateWithRetry(request, attempt + 1);
      }

      // All retries exhausted
      throw new Error(`Failed to evaluate answer after ${this.MAX_RETRIES} attempts: ${errorMessage}`);
    }
  }

  /**
   * Simulate progress for better UX during AI processing
   */
  private simulateProgress(onProgress: (percent: number) => void, estimatedTime: number) {
    let progress = 0;
    const interval = 100; // Update every 100ms
    const increment = (interval / estimatedTime) * 100;

    const timer = setInterval(() => {
      progress += increment;

      // Slow down as we approach 100%
      if (progress >= 90) {
        progress += increment * 0.1;
      }

      if (progress >= 95) {
        clearInterval(timer);
        return;
      }

      onProgress(Math.min(progress, 95)); // Never quite reach 100% until actually complete
    }, interval);

    // Store timer for potential cleanup
    return timer;
  }

  /**
   * Generate cache key from question and answer
   */
  private getCacheKey(question: string, answer: string): string {
    // Simple hash function for cache key
    const str = `${question}_${answer}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `eval_${hash}`;
  }

  /**
   * Get cached evaluation result if still valid
   */
  private getFromCache(key: string): AIEvaluationResponse | null {
    const cached = this.cache[key];

    if (!cached) {
      return null;
    }

    // Check if cache is still valid
    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      delete this.cache[key];
      return null;
    }

    return cached.result;
  }

  /**
   * Add evaluation result to cache
   */
  private addToCache(key: string, result: AIEvaluationResponse): void {
    this.cache[key] = {
      result,
      timestamp: Date.now()
    };

    // Clean up old cache entries
    this.cleanupCache();
  }

  /**
   * Remove expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(key => {
      if (now - this.cache[key].timestamp > this.CACHE_TTL) {
        delete this.cache[key];
      }
    });
  }

  /**
   * Clear all cached evaluations
   */
  clearCache(): void {
    this.cache = {};
    logger.info('Evaluation cache cleared');
  }

  /**
   * Helper function to sleep for a specified duration
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: Object.keys(this.cache).length,
      keys: Object.keys(this.cache)
    };
  }
}

// Export singleton instance
export const aiEvaluationService = new AIEvaluationService();
export default aiEvaluationService;
